import React from 'react';
import { PredictPatient, PredictProgram, WorkflowStage } from '~/types/predict';
import { OwnershipFilter, PatientBoardConfig, PatientCardVM, PhaseDef } from '~/components/patient-board/types';

// Short program labels (match the existing Predict score column density).
const PROGRAM_LABELS: Record<PredictProgram, string> = {
  [PredictProgram.HOME_HEALTH]: 'Home Health',
  [PredictProgram.HOSPITAL_AT_HOME]: 'Hospital at Home',
  [PredictProgram.SNF_AT_HOME]: 'SNF at Home',
  [PredictProgram.HOSPICE]: 'Hospice',
  [PredictProgram.INFUSION_AT_HOME]: 'Infusion at Home',
  [PredictProgram.PALLIATIVE]: 'Palliative',
};

// Predict's three phases. Palette reused from the Coordinate phase chips so the
// two features look identical.
export const PREDICT_PHASES: PhaseDef[] = [
  { key: 'predict',  label: 'Predict',  bgcolor: '#e3f2fd', color: '#1565c0' },
  { key: 'qualify',  label: 'Qualify',  bgcolor: '#e8eaf6', color: '#283593' },
  { key: 'transfer', label: 'Transfer', bgcolor: '#e0f7f4', color: '#005a4e' },
];

export type PredictPhaseKey = 'predict' | 'qualify' | 'transfer';

// Collapse the 5 workflow stages into the 3 board phases.
// (DISQUALIFIED patients are filtered out of the list before mapping.)
export const stageToPhase = (stage: WorkflowStage): PredictPhaseKey => {
  switch (stage) {
    case WorkflowStage.TRIAGE:       return 'predict';
    case WorkflowStage.ELIGIBILITY:  return 'qualify';
    case WorkflowStage.ACCEPTANCE:   return 'qualify';
    case WorkflowStage.TRANSITION:   return 'transfer';
    case WorkflowStage.DISQUALIFIED: return 'predict';
    default:                         return 'predict';
  }
};

const initialsOf = (name: string): string =>
  name.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const formatDate = (ms: number): string =>
  new Date(ms).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

export const mapPredictPatient = (p: PredictPatient): PatientCardVM => {
  const name = `${p.firstName} ${p.lastName}`;
  const score = p.programScores[p.recommendedProgram] ?? 0;
  const assignedName = p.assignment?.userId ? p.assignment.userName : null;

  // Predict has no admit date — derive an expected discharge from remaining LOS.
  const remainingDays = Math.max(0, p.expectedLOS - p.currentLOS);
  const expectedMs = Date.now() + remainingDays * 86_400_000;

  const dob = new Date(p.dateOfBirth).toLocaleDateString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
  });

  return {
    id: p.id,
    name,
    age: p.age,
    dob,
    mrn: p.mrn,
    scoreCell: { label: PROGRAM_LABELS[p.recommendedProgram], score },
    locationPrimary: p.department,
    locationSecondary: p.facility,
    physician: p.attendingPhysician,
    diagnosis: p.primaryDiagnosis,
    expectedDischarge: formatDate(expectedMs),
    expectedDischargeMs: expectedMs,
    currentLOS: p.currentLOS,
    phaseKey: stageToPhase(p.currentStage),
    urgency: undefined,
    assignedToName: assignedName,
    assignedToInitials: assignedName ? (p.assignment?.userInitials ?? initialsOf(assignedName)) : null,
    headerInfoLines: [
      React.createElement(React.Fragment, null,
        `DOB ${dob} · Age ${p.age} · ${p.gender} · `,
        React.createElement('strong', null, `${p.facility} · ${p.department}`),
      ),
      `Expected LOS ${p.expectedLOS} days · Current LOS ${p.currentLOS} days · ${p.insuranceType} · MRN ${p.mrn}`,
      React.createElement(React.Fragment, null,
        React.createElement('strong', null, 'Attending:'), ' ',
        React.createElement('strong', null, p.attendingPhysician),
        ` · ${p.primaryDiagnosis}`,
      ),
    ],
    losTargetDays: p.expectedLOS,
    insuranceLine: p.insuranceType,
    attending: p.attendingPhysician,
  };
};

const titleFor = (ownership: OwnershipFilter): string =>
  ownership === 'MY' ? 'My Patients' :
  ownership === 'UNASSIGNED' ? 'Unassigned Patients' :
  'All Patients';

export const makePredictConfig = (onTake: (id: string) => void): PatientBoardConfig => ({
  routeBase: '/predict',
  title: titleFor,
  phases: PREDICT_PHASES,
  showUrgency: false,
  scoreColumnLabel: 'Score',
  onTake,
  searchText: (vm) => [vm.name, vm.mrn, vm.locationSecondary, vm.physician, vm.diagnosis].join(' '),
});
